import { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';

interface RewardAnimationVideoProps {
  size: 164 | 200 | 220;
  className?: string;
}

const RewardAnimationVideo = ({ size, className }: RewardAnimationVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isReady, setIsReady] = useState(false);
  const sizeClass = size === 220
    ? 'h-[220px] w-[220px]'
    : size === 200
      ? 'h-[200px] w-[200px]'
      : 'h-[164px] w-[164px]';

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    const startPlayback = () => {
      void video.play()
        .then(() => setIsReady(true))
        .catch(() => {
          // Reveal the decoded frame even if a WebView rejects autoplay.
          setIsReady(true);
        });
    };

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      startPlayback();
      return;
    }

    video.addEventListener('canplay', startPlayback, { once: true });
    video.load();
    return () => video.removeEventListener('canplay', startPlayback);
  }, []);

  // Keep the video out of the DOM until it has decoded enough data to play.
  if (!isReady) {
    return (
      <video
        ref={videoRef}
        className="absolute h-px w-px opacity-0 pointer-events-none"
        src="/reward_animation.mp4"
        autoPlay
        muted
        playsInline
        preload="auto"
        controls={false}
        disablePictureInPicture
        aria-hidden="true"
      />
    );
  }

  return (
    <video
      ref={videoRef}
      className={cn(sizeClass, 'object-contain', className)}
      src="/reward_animation.mp4"
      autoPlay
      muted
      playsInline
      preload="auto"
      controls={false}
      disablePictureInPicture
      aria-hidden="true"
    />
  );
};

export default RewardAnimationVideo;
